import React from 'react'
import { Button, Dialog, Field, IconButton, Input, Switch } from '@/components/ds'
import { Ic } from '@/components/icons'
import { Box, BoxHeader, BoxLabel, BoxRow, Pill, QueryState } from '@/components/common'
import { listCampaignStats, listIdentities } from '@/lib/db'
import { deleteIdentity, saveIdentity, testIdentity, type IdentityInput } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { useToast } from '@/lib/toast'
import { isValidEmail } from '@/lib/format'
import type { SmtpAccountSafe } from '@shared/types'
import type { ScreenProps } from '@/App'

/**
 * SMTP mailboxes we send from.
 *
 * The password is write-only from the browser's point of view: it goes out over
 * HTTPS to /api/identities, which encrypts it with ENCRYPTION_KEY before it
 * touches Postgres. Nothing ever reads it back — the list view only knows
 * whether a password exists.
 */
// narrow is unused since the flat list wraps naturally at any width.
export function IdentitiesScreen(_props: ScreenProps) {
  const toast = useToast()
  const [editing, setEditing] = React.useState<SmtpAccountSafe | 'new' | null>(null)
  const [confirmDelete, setConfirmDelete] = React.useState<SmtpAccountSafe | null>(null)
  const [testing, setTesting] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  const identities = useAsync(() => listIdentities(), [])
  const stats = useAsync(() => listCampaignStats(), [])

  const rows = identities.data ?? []

  async function runTest(id: string, label: string) {
    setTesting(id)
    try {
      const result = await testIdentity(id)
      toast(result.ok ? 'success' : 'error', result.ok ? `${label} connected` : `${label} failed`, result.detail)
    } catch (e) {
      toast('error', 'Test failed', e instanceof Error ? e.message : String(e))
    } finally {
      setTesting(null)
    }
  }

  async function remove(row: SmtpAccountSafe) {
    setBusy(true)
    try {
      await deleteIdentity(row.id)
      toast('info', `${row.label} removed`)
      setConfirmDelete(null)
      identities.reload()
    } catch (e) {
      toast('error', 'Delete failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', flex: 1 }}>
          Each identity is one SMTP mailbox. Passwords are encrypted server-side and never sent
          back to this page.
        </p>
        <Button size="sm" icon={<Ic.plus size={14} />} onClick={() => setEditing('new')}>
          Add identity
        </Button>
      </div>

      <QueryState
        loading={identities.loading}
        error={identities.error}
        empty={rows.length === 0}
        emptyTitle="No sending identities"
        emptyDescription="Add the SMTP mailbox you want campaigns to send from — host, port 587, username, and password."
        emptyAction={
          <Button icon={<Ic.plus size={14} />} onClick={() => setEditing('new')}>
            Add identity
          </Button>
        }
        onRetry={identities.reload}
      >
        <Box>
          <BoxHeader>
            <BoxLabel style={{ flex: 1 }}>Sender</BoxLabel>
          </BoxHeader>
          {rows.map((row, i) => {
            // Today's volume across the campaigns actually bound to this identity.
            const mine = (stats.data ?? []).filter((s) => s.smtp_account_id === row.id)
            const usedToday = mine.reduce((sum, s) => sum + Number(s.sent_today ?? 0), 0)
            const capToday = mine.reduce((sum, s) => sum + Number(s.daily_cap ?? 0), 0)

            return (
              <BoxRow key={row.id} last={i === rows.length - 1}>
                <span
                  style={{
                    width: 26,
                    height: 26,
                    flex: 'none',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--accent-soft)',
                    color: 'var(--accent-text)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Ic.atSign size={14} />
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.from_email}
                    </span>
                    {row.is_default && (
                      <Pill bg="var(--accent-soft)" fg="var(--accent-text)">Default</Pill>
                    )}
                    {!row.has_password && (
                      <Pill bg="var(--status-pending-bg)" fg="var(--status-pending-fg)" weight={700}>
                        No password
                      </Pill>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.host}:{row.port} · {row.username} · {row.label} · {mine.length} campaign
                    {mine.length === 1 ? '' : 's'} ·{' '}
                    <span className="tnum">
                      sent today {usedToday}
                      {capToday ? `/${capToday}` : ''}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  disabled={testing === row.id || !row.has_password}
                  onClick={() => runTest(row.id, row.label)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    cursor: testing === row.id || !row.has_password ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 12.5,
                    color:
                      testing === row.id || !row.has_password
                        ? 'var(--text-faint)'
                        : 'var(--accent-text)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {testing === row.id ? 'Testing…' : 'Test connection'}
                </button>
                <IconButton label="Edit" size="sm" onClick={() => setEditing(row)}>
                  <Ic.pencil size={15} />
                </IconButton>
                <IconButton label="Delete" size="sm" onClick={() => setConfirmDelete(row)}>
                  <Ic.trash size={15} />
                </IconButton>
              </BoxRow>
            )
          })}
        </Box>
      </QueryState>

      {editing && (
        <IdentityDialog
          identity={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(label, created) => {
            setEditing(null)
            toast('success', created ? `${label} added` : `${label} saved`)
            identities.reload()
          }}
        />
      )}

      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={`Remove ${confirmDelete?.label}?`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => confirmDelete && remove(confirmDelete)}
            >
              Remove
            </Button>
          </>
        }
      >
        Campaigns pointing at this identity fall back to the default one. Nothing in the queue is
        deleted.
      </Dialog>
    </div>
  )
}

// -----------------------------------------------------------------------------

function IdentityDialog({
  identity,
  onClose,
  onSaved,
}: {
  identity: SmtpAccountSafe | null
  onClose: () => void
  onSaved: (label: string, created: boolean) => void
}) {
  const toast = useToast()
  const [draft, setDraft] = React.useState<IdentityInput>(() =>
    identity
      ? {
          id: identity.id,
          label: identity.label,
          host: identity.host,
          port: identity.port,
          username: identity.username,
          password: '',
          from_name: identity.from_name,
          from_email: identity.from_email,
          reply_to: identity.reply_to ?? '',
          sender_title: identity.sender_title ?? '',
          is_default: identity.is_default,
        }
      : {
          label: '',
          host: '',
          port: 587,
          username: '',
          password: '',
          from_name: '',
          from_email: '',
          reply_to: '',
          sender_title: '',
          is_default: false,
        },
  )
  const [busy, setBusy] = React.useState(false)

  const set = <K extends keyof IdentityInput>(key: K, value: IdentityInput[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  async function save() {
    if (!draft.label.trim() || !draft.host.trim() || !draft.username.trim()) {
      toast('error', 'Label, host, and username are required')
      return
    }
    if (!isValidEmail(draft.from_email)) {
      toast('error', 'From address looks wrong', draft.from_email || 'It cannot be blank')
      return
    }
    if (draft.reply_to?.trim() && !isValidEmail(draft.reply_to)) {
      toast('error', 'Default reply-to looks wrong', draft.reply_to)
      return
    }
    if (!identity && !draft.password) {
      toast('error', 'A password is required for a new identity')
      return
    }

    setBusy(true)
    try {
      // An empty password on an edit means "leave the stored one alone".
      const payload = { ...draft }
      if (identity && !payload.password) delete payload.password
      await saveIdentity(payload)
      onSaved(draft.label, !identity)
    } catch (e) {
      toast('error', 'Save failed', e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={520}
      title={identity ? `Edit ${identity.label}` : 'Add sending identity'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={save}>
            {busy ? 'Saving…' : identity ? 'Save changes' : 'Add identity'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Label" hint="Just for you — shown in campaign pickers">
          <Input value={draft.label} onChange={(v) => set('label', v)} placeholder="Jess @ Acme" autoFocus />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <Field label="SMTP host">
            <Input value={draft.host} onChange={(v) => set('host', v)} placeholder="mail.acme.co" />
          </Field>
          <Field label="Port" hint="587 = STARTTLS">
            <Input
              type="number"
              value={String(draft.port)}
              onChange={(v) => set('port', Number(v) || 587)}
            />
          </Field>
        </div>

        <Field label="Username">
          <Input
            value={draft.username}
            onChange={(v) => set('username', v)}
            placeholder="jess@acme.co"
            autoComplete="off"
          />
        </Field>

        <Field
          label="Password"
          hint={
            identity
              ? 'Leave blank to keep the stored password. It is never shown here.'
              : 'Encrypted with ENCRYPTION_KEY before it reaches the database'
          }
        >
          <Input
            type="password"
            value={draft.password ?? ''}
            onChange={(v) => set('password', v)}
            placeholder={identity?.has_password ? '••••••••  (unchanged)' : ''}
            autoComplete="new-password"
          />
        </Field>

        <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field
            label="Display name"
            hint="How the sender appears, e.g. StuHelp"
          >
            <Input
              value={draft.from_name}
              onChange={(v) => set('from_name', v)}
              placeholder="StuHelp"
            />
          </Field>
          <Field label="From address" hint="The authenticated mailbox itself — never spoofed">
            <Input
              type="email"
              value={draft.from_email}
              onChange={(v) => set('from_email', v)}
              placeholder="jess@acme.co"
            />
          </Field>
        </div>

        <Field
          label="Default reply-to (optional)"
          hint="Replies go here instead of the sending mailbox. Campaigns can override it."
        >
          <Input
            type="email"
            value={draft.reply_to ?? ''}
            onChange={(v) => set('reply_to', v)}
            placeholder="hiring@company.com"
          />
        </Field>

        <Field
          label="Sender title (optional)"
          hint="Fills {{sender_title}} in templates. Campaigns can override it."
        >
          <Input
            value={draft.sender_title ?? ''}
            onChange={(v) => set('sender_title', v)}
            placeholder="Talent Team"
          />
        </Field>

        {/* A pill, not a hairline row — this is a choice, not another field. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '11px 13px',
            borderRadius: 12,
            background: draft.is_default ? 'var(--accent-soft)' : 'var(--surface-sunken)',
            border: `1px solid ${
              draft.is_default ? 'var(--accent-soft-border)' : 'var(--border-default)'
            }`,
            transition: 'background var(--dur-fast) var(--ease-out)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
              Default identity
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
              Used by campaigns that don't pick one
            </div>
          </div>
          <Switch checked={!!draft.is_default} onChange={(v) => set('is_default', v)} />
        </div>
      </div>
    </Dialog>
  )
}
